/**
 * 頁 6 — 發貨單詳情／收貨對數（`ui-spec` 頁 6）
 *
 * · 供應商 → **只讀**（睇自己嘅單、申報內容、入庫結果）
 * · 物流／職員／管理員 → 未收貨（`declared`）就出**對數表單**（`IntakeVerifyForm`）
 * ⚠️ 權限由後端把關；前端只係唔顯示做唔到嘅嘢。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import IntakeStatusChip from '../components/IntakeStatusChip';
import IntakeVerifyForm from '../components/IntakeVerifyForm';
import { btnGhost, card, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type IntakeBatch, type IntakeLine, type Me } from '../lib/api';

function Field({ labelKey, value }: { labelKey: string; value: string }) {
  const { t } = useTranslation();
  return (
    <p style={{ margin: '2px 0', fontSize: '14px' }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>{t(labelKey)}：</span>
      {value}
    </p>
  );
}

export default function IntakeDetail({ user }: { user: Me }) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [batch, setBatch] = useState<IntakeBatch | null>(null);
  const [lines, setLines] = useState<IntakeLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.intake(id);
      setBatch(res.batch);
      setLines(res.lines ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const canVerify = !!batch && batch.status === 'declared' && user.role !== 'supplier';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('intake.detailTitle')}</h2>
        <Link to="/intakes" style={btnGhost}>
          {t('intake.back')}
        </Link>
      </div>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}
        </p>
      )}

      {batch && (
        <>
          <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '16px' }}>{batch.batchNo}</strong>
              <IntakeStatusChip status={batch.status} />
            </div>
            <div style={{ marginTop: '8px' }}>
              {batch.declaredShipDate && (
                <Field labelKey="intake.declaredShipDate" value={batch.declaredShipDate} />
              )}
              {batch.receivedDate && (
                <Field labelKey="intake.receivedDate" value={batch.receivedDate} />
              )}
              <Field labelKey="intake.declaredCbm" value={String(batch.declaredCbm)} />
              {batch.receivedCbm > 0 && (
                <Field labelKey="intake.receivedCbm" value={String(batch.receivedCbm)} />
              )}
              {batch.handlingFee > 0 && (
                <Field labelKey="intake.handlingFee" value={`HK$${batch.handlingFee}`} />
              )}
              {batch.note && <Field labelKey="intake.note" value={batch.note} />}
            </div>

            <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0 }}>
              {lines.map((l) => (
                <li
                  key={l.id}
                  style={{ padding: '8px 0', borderTop: '1px solid var(--color-divider)' }}
                >
                  <strong>{l.itemName ?? '—'}</strong>
                  <span style={hintStyle}> {l.barcode ?? ''}</span>
                  <p style={hintStyle}>
                    {t('intake.declaredCartons')}：{l.declaredCartons}
                    {l.receivedCartons > 0 ? ` · ${t('intake.receivedCartons')}：${l.receivedCartons}` : ''}
                    {l.damagedCartons > 0 ? ` · ${t('intake.damagedCartons')}：${l.damagedCartons}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          {canVerify ? (
            <IntakeVerifyForm batch={batch} lines={lines} onDone={load} />
          ) : (
            <p style={{ ...hintStyle, marginTop: '12px' }}>
              {user.role === 'supplier' ? t('intake.readOnly') : t('intake.damagedNote')}
            </p>
          )}
        </>
      )}
    </>
  );
}
